import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { getSettingsService } from "../../services/appSetting.service";
import { useTranslation } from "react-i18next";

const BusinessSettingsContext = createContext({
  settings: null,
  setSettings: () => {},
  isSettingsLoading: false,
});

export const BusinessSettingsProvider = ({ businessUuid, children }) => {
  const { i18n } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [isSettingsLoading, setIsSettingsLoading] = useState(Boolean(businessUuid));

  useEffect(() => {
    let isMounted = true;

    if (!businessUuid) {
      setSettings(null);
      setIsSettingsLoading(false);
      return undefined;
    }

    setSettings(null);
    setIsSettingsLoading(true);

    getSettingsService(businessUuid)
      .then((response) => {
        if (isMounted) {
          setSettings(response?.data || null);
        }
      })
      .catch((error) => {
        if (error.response?.status !== 404) {
          console.error("Failed to load business feature settings:", error);
        }
        if (isMounted) {
          setSettings(null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsSettingsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [businessUuid]);

  useEffect(() => {
    const language = settings?.appSettings?.language;
    if (language && language !== i18n.language) {
      i18n.changeLanguage(language);
      localStorage.setItem("lng", language);
    }

    const themeMode = settings?.appSettings?.themeMode;
    if (themeMode) {
      const isDark =
        themeMode === "dark" ||
        (themeMode === "system" &&
          window.matchMedia("(prefers-color-scheme: dark)").matches);
      document.documentElement.classList.toggle("dark", isDark);
    }
  }, [settings?.appSettings?.language, settings?.appSettings?.themeMode]);

  return (
    <BusinessSettingsContext.Provider
      value={{ settings, setSettings, isSettingsLoading }}
    >
      {children}
    </BusinessSettingsContext.Provider>
  );
};

export const useBusinessSettings = () => useContext(BusinessSettingsContext);

const isSettingEnabled = (settings, setting) => {
  const settingPaths = Array.isArray(setting) ? setting : [setting];
  return settingPaths.some((settingPath) => {
    const path = settingPath.includes(".")
      ? settingPath
      : `appSettings.${settingPath}`;
    const value = path.split(".").reduce((current, key) => current?.[key], settings);
    return value !== false;
  });
};

export const FeatureSettingRoute = ({
  setting,
  children,
  disabledMessage,
}) => {
  const { bussinessUuid } = useParams();
  const { settings, isSettingsLoading } = useBusinessSettings();

  if (isSettingsLoading) {
    return <div className="min-h-[50vh]" aria-label="Loading feature settings" />;
  }

  if (!isSettingEnabled(settings, setting)) {
    if (disabledMessage) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 text-center text-gray-600 dark:text-gray-300">
          {disabledMessage}
        </div>
      );
    }
    return <Navigate to={`/${bussinessUuid}/dashboard`} replace />;
  }

  return children;
};

export const BusinessFeatureRoute = ({
  setting,
  children,
  disabledMessage,
}) => {
  const { bussinessUuid } = useParams();
  return (
    <BusinessSettingsProvider businessUuid={bussinessUuid}>
      <FeatureSettingRoute
        setting={setting}
        disabledMessage={disabledMessage}
      >
        {children}
      </FeatureSettingRoute>
    </BusinessSettingsProvider>
  );
};
